import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  LayoutDashboard, Inbox, GitBranch, UtensilsCrossed, Package, Images, Truck,
  Users, Building2, Cctv, Sparkles, ScrollText, Settings as Cog, LogOut, Menu, X, Bell,
} from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/pipeline", label: "Event Pipeline", icon: GitBranch },
  { to: "/admin/menu", label: "Menu Management", icon: UtensilsCrossed },
  { to: "/admin/packages", label: "Menu Packages", icon: Package },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/bulk", label: "Bulk & Contracts", icon: Truck },
  { to: "/admin/testimonials", label: "Testimonials", icon: ScrollText },
  { to: "/admin/forecast", label: "AI Forecast", icon: Sparkles },
  { to: "/admin/staff", label: "Staff & Roles", icon: Users, admin: true },
  { to: "/admin/branches", label: "Branches & Brands", icon: Building2, admin: true },
  { to: "/admin/cctv", label: "CCTV", icon: Cctv, admin: true },
  { to: "/admin/activity", label: "Activity Log", icon: ScrollText },
  { to: "/admin/settings", label: "Website Settings", icon: Cog, admin: true },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [showN, setShowN] = useState(false);

  useEffect(() => {
    if (!loading && (user === false || !user?.email)) navigate("/admin/login", { replace: true });
  }, [user, loading]);

  useEffect(() => {
    if (user?.email) api.get("/admin/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  }, [user]);

  if (loading || !user?.email) {
    return <div className="min-h-screen bg-[#1C1210] flex items-center justify-center text-gold">Loading DineOS…</div>;
  }

  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const links = LINKS.filter((l) => !l.admin || isAdmin);
  const unread = notifs.filter((n) => !n.read).length;

  const markRead = () => { api.post("/admin/notifications/read").catch(() => {}); setNotifs(notifs.map((n) => ({ ...n, read: true }))); };

  return (
    <div className="min-h-screen bg-[#171010] text-ivory flex">
      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 z-50 h-screen w-64 bg-[#1C1210] border-r border-gold/15 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-gold/15 flex items-center gap-3">
          <img src="/logo.jpeg" alt="logo" className="h-11 rounded" />
          <div><p className="text-gold text-[0.6rem] tracking-[0.25em]">OPEREXA</p><p className="font-serif text-lg leading-none text-ivory">DineOS</p></div>
          <button className="ml-auto lg:hidden text-ivory/60" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto dark-scroll p-3 space-y-0.5">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm transition-colors ${isActive ? "bg-gold/15 text-gold" : "text-ivory/60 hover:text-ivory hover:bg-white/5"}`}>
              <l.icon className="w-[18px] h-[18px]" />{l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-gold/15">
          <div className="px-3 py-2 mb-1">
            <p className="text-sm text-ivory truncate">{user.name}</p>
            <p className="text-[0.68rem] text-gold/70 uppercase tracking-wide">{user.role?.replace("_", " ")}</p>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm text-ivory/60 hover:text-red-400 hover:bg-red-500/10" data-testid="admin-logout"><LogOut className="w-[18px] h-[18px]" /> Sign Out</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-30 bg-[#1C1210]/90 backdrop-blur border-b border-gold/15 px-4 sm:px-6 py-3 flex items-center justify-between">
          <button className="lg:hidden text-ivory" onClick={() => setOpen(true)}><Menu className="w-6 h-6" /></button>
          <p className="hidden sm:block text-ivory/40 text-sm">Foodie Connect Caterer · Operations</p>
          <div className="relative ml-auto">
            <button onClick={() => { setShowN(!showN); if (!showN) markRead(); }} className="relative text-ivory/70 hover:text-gold p-2" data-testid="admin-notifications">
              <Bell className="w-5 h-5" />
              {unread > 0 && <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-gold text-velvet text-[0.6rem] flex items-center justify-center">{unread}</span>}
            </button>
            {showN && (
              <div className="absolute right-0 mt-2 w-80 bg-[#241a17] border border-gold/20 rounded-xl shadow-2xl max-h-96 overflow-y-auto dark-scroll">
                <p className="px-4 py-3 text-gold text-sm border-b border-gold/15">Notifications</p>
                {notifs.length ? notifs.map((n) => (
                  <div key={n.id} className="px-4 py-3 border-b border-white/5 hover:bg-white/5">
                    <p className="text-sm text-ivory">{n.title}</p>
                    <p className="text-xs text-ivory/50">{n.message}</p>
                  </div>
                )) : <p className="px-4 py-6 text-center text-ivory/40 text-sm">No notifications</p>}
              </div>
            )}
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]"><Outlet /></main>
      </div>
    </div>
  );
}
